import { create } from "zustand";
import { useCallback, useMemo } from "react";

/**
 * Types of items that can be multi-selected in the sidebar.
 * Only one type can be selected at a time.
 */
export type SelectableItemType = "dashboard" | "tracker" | "enrichment" | "alert" | "widget" | "investigation" | "conversation" | "report";

/**
 * Multi-select store state
 */
interface MultiSelectState {
  /** IDs of currently selected items */
  selectedIds: string[];
  /** Last selected item ID (for range selection with Shift) */
  lastSelectedId: string | null;
  /** Type of items currently selected */
  itemType: SelectableItemType | null;
}

/**
 * Multi-select store actions
 */
interface MultiSelectActions {
  /** Set the selected IDs directly */
  setSelectedIds: (ids: string[]) => void;
  /** Add an item to the selection */
  addToSelection: (id: string, itemType: SelectableItemType) => void;
  /** Remove an item from the selection */
  removeFromSelection: (id: string) => void;
  /** Toggle an item's selection state */
  toggleSelection: (id: string, itemType: SelectableItemType) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Set the last selected ID (for range selection) */
  setLastSelectedId: (id: string | null) => void;
  /** Set the item type */
  setItemType: (type: SelectableItemType | null) => void;
  /** Select a range of items (for Shift+click) */
  selectRange: (
    targetId: string,
    orderedIds: string[],
    itemType: SelectableItemType
  ) => void;
  /** Select all items */
  selectAll: (ids: string[], itemType: SelectableItemType) => void;
}

type MultiSelectStore = MultiSelectState & MultiSelectActions;

/**
 * Zustand store for multi-select state.
 * This is separate from the sidebar store to keep concerns separated
 * and avoid unnecessary re-renders.
 */
export const useMultiSelectStore = create<MultiSelectStore>((set, get) => ({
  // Initial state
  selectedIds: [],
  lastSelectedId: null,
  itemType: null,

  // Actions
  setSelectedIds: (ids) => set({ selectedIds: ids }),

  addToSelection: (id, itemType) =>
    set((state) => {
      // If changing item type, clear previous selection
      if (state.itemType !== null && state.itemType !== itemType) {
        return {
          selectedIds: [id],
          lastSelectedId: id,
          itemType,
        };
      }

      // Add to existing selection if not already present
      if (state.selectedIds.includes(id)) {
        return { lastSelectedId: id };
      }

      return {
        selectedIds: [...state.selectedIds, id],
        lastSelectedId: id,
        itemType,
      };
    }),

  removeFromSelection: (id) =>
    set((state) => {
      const newIds = state.selectedIds.filter((i) => i !== id);
      return {
        selectedIds: newIds,
        // Clear item type if no items selected
        itemType: newIds.length === 0 ? null : state.itemType,
        // Update lastSelectedId if the removed item was the last selected
        lastSelectedId:
          state.lastSelectedId === id
            ? newIds.length > 0
              ? newIds[newIds.length - 1]
              : null
            : state.lastSelectedId,
      };
    }),

  toggleSelection: (id, itemType) =>
    set((state) => {
      // If changing item type, start fresh with this item
      if (state.itemType !== null && state.itemType !== itemType) {
        return {
          selectedIds: [id],
          lastSelectedId: id,
          itemType,
        };
      }

      const isSelected = state.selectedIds.includes(id);

      if (isSelected) {
        const newIds = state.selectedIds.filter((i) => i !== id);
        return {
          selectedIds: newIds,
          itemType: newIds.length === 0 ? null : itemType,
          lastSelectedId:
            state.lastSelectedId === id
              ? newIds.length > 0
                ? newIds[newIds.length - 1]
                : null
              : state.lastSelectedId,
        };
      } else {
        return {
          selectedIds: [...state.selectedIds, id],
          lastSelectedId: id,
          itemType,
        };
      }
    }),

  clearSelection: () =>
    set({
      selectedIds: [],
      lastSelectedId: null,
      itemType: null,
    }),

  setLastSelectedId: (id) => set({ lastSelectedId: id }),

  setItemType: (type) => set({ itemType: type }),

  selectRange: (targetId, orderedIds, itemType) =>
    set((state) => {
      // If changing item type, start fresh
      if (state.itemType !== null && state.itemType !== itemType) {
        return {
          selectedIds: [targetId],
          lastSelectedId: targetId,
          itemType,
        };
      }

      const lastId = state.lastSelectedId;

      // If no previous selection, just select the target
      if (!lastId) {
        return {
          selectedIds: [targetId],
          lastSelectedId: targetId,
          itemType,
        };
      }

      // Find indices in the ordered list
      const lastIndex = orderedIds.indexOf(lastId);
      const targetIndex = orderedIds.indexOf(targetId);

      // If either item not found, just select the target
      if (lastIndex === -1 || targetIndex === -1) {
        return {
          selectedIds: [targetId],
          lastSelectedId: targetId,
          itemType,
        };
      }

      // Get range of items to select
      const startIndex = Math.min(lastIndex, targetIndex);
      const endIndex = Math.max(lastIndex, targetIndex);
      const rangeIds = orderedIds.slice(startIndex, endIndex + 1);

      // Merge with existing selection (union)
      const newSelectedIds = Array.from(
        new Set([...state.selectedIds, ...rangeIds])
      );

      return {
        selectedIds: newSelectedIds,
        lastSelectedId: targetId,
        itemType,
      };
    }),

  selectAll: (ids, itemType) =>
    set({
      selectedIds: ids,
      lastSelectedId: ids.length > 0 ? ids[ids.length - 1] : null,
      itemType: ids.length > 0 ? itemType : null,
    }),
}));

/**
 * Hook to use multi-select functionality.
 * Provides a clean interface for components to interact with the multi-select store.
 */
export function useMultiSelect() {
  const store = useMultiSelectStore();

  /**
   * Check if an item is selected
   */
  const isSelected = useCallback(
    (id: string): boolean => {
      return store.selectedIds.includes(id);
    },
    [store.selectedIds]
  );

  /**
   * Handle click on an item with modifier key detection.
   * Returns true if the click was handled as a selection action,
   * false if it should proceed with normal navigation.
   */
  const handleItemClick = useCallback(
    (
      id: string,
      itemType: SelectableItemType,
      e: React.MouseEvent,
      orderedIds: string[]
    ): boolean => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      const isShift = e.shiftKey;

      // If no modifier key, this is a normal click - don't handle selection
      if (!isCtrlOrCmd && !isShift) {
        return false;
      }

      // Prevent default behavior and text selection
      e.preventDefault();

      if (isShift) {
        // Shift+click: range selection
        store.selectRange(id, orderedIds, itemType);
      } else if (isCtrlOrCmd) {
        // Ctrl/Cmd+click: toggle selection
        store.toggleSelection(id, itemType);
      }

      return true;
    },
    [store]
  );

  /**
   * Get the count of selected items
   */
  const selectionCount = useMemo(
    () => store.selectedIds.length,
    [store.selectedIds]
  );

  /**
   * Check if there's an active selection
   */
  const hasSelection = useMemo(
    () => store.selectedIds.length > 0,
    [store.selectedIds]
  );

  return {
    // State
    selectedIds: store.selectedIds,
    lastSelectedId: store.lastSelectedId,
    itemType: store.itemType,
    selectionCount,
    hasSelection,

    // Actions
    addToSelection: store.addToSelection,
    removeFromSelection: store.removeFromSelection,
    toggleSelection: store.toggleSelection,
    clearSelection: store.clearSelection,
    selectRange: store.selectRange,
    selectAll: store.selectAll,

    // Helpers
    isSelected,
    handleItemClick,
  };
}

// Export types
export type { MultiSelectState, MultiSelectActions, MultiSelectStore };
